import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import {
  AddLeafNodeRequest,
  ChartOfAccountsDirection,
  ChartOfAccountsNodeViewModel,
  ChartOfAccountsTreeViewModel,
  IN_NATURES,
  OUT_NATURES,
} from './entities/chart-of-accounts.model';
import { ChartOfAccountsService } from './infra/chart-of-accounts.service';

export const LOAD_ERROR_FALLBACK = 'TREASURY.CHART_OF_ACCOUNTS.ERRORS.LOAD_FAILED';
export const VALIDATION_ERROR_FALLBACK = 'TREASURY.CHART_OF_ACCOUNTS.ERRORS.GENERIC_VALIDATION';
export const NOT_FOUND_ERROR_FALLBACK = 'TREASURY.CHART_OF_ACCOUNTS.ERRORS.NOT_FOUND';

export const KNOWN_ERROR_MAP: Record<string, string> = {
  'Nó pai inválido ou já é uma conta folha': 'TREASURY.CHART_OF_ACCOUNTS.ERRORS.INVALID_PARENT_OR_LEAF',
};

const MUTATION_OPS = ['addLeafNode', 'activateNode', 'deactivateNode'] as const;
const ALL_OPS = ['loadTree', ...MUTATION_OPS] as const;

interface ChartOfAccountsStoreState {
  tree: ChartOfAccountsTreeViewModel | null;
  lastCreatedNodeId: string | null;
  lastToggledNodeId: string | null;
}

const initialState: ChartOfAccountsStoreState = {
  tree: null,
  lastCreatedNodeId: null,
  lastToggledNodeId: null,
};

const updateNodeInTree = (
  tree: ChartOfAccountsTreeViewModel,
  nodeId: string,
  updater: (node: ChartOfAccountsNodeViewModel) => ChartOfAccountsNodeViewModel,
): ChartOfAccountsTreeViewModel => {
  let mutated = false;

  const visit = (
    node: ChartOfAccountsNodeViewModel,
  ): ChartOfAccountsNodeViewModel => {
    if (node.NodeId === nodeId) {
      mutated = true;
      return updater(node);
    }
    if (!node.Children?.length) return node;
    const newChildren = node.Children.map(visit);
    if (newChildren.some((child, i) => child !== node.Children[i])) {
      return { ...node, Children: newChildren };
    }
    return node;
  };

  const newRoots = tree.Roots.map(visit);
  return mutated ? { Roots: newRoots } : tree;
};

const appendChildInTree = (
  tree: ChartOfAccountsTreeViewModel,
  parentNodeId: string,
  newChild: ChartOfAccountsNodeViewModel,
): ChartOfAccountsTreeViewModel =>
  updateNodeInTree(tree, parentNodeId, (parent) => ({
    ...parent,
    IsLeaf: false,
    Children: [...parent.Children, newChild],
  }));

const collectNodes = (
  roots: readonly ChartOfAccountsNodeViewModel[],
  predicate: (node: ChartOfAccountsNodeViewModel) => boolean,
): ChartOfAccountsNodeViewModel[] => {
  const out: ChartOfAccountsNodeViewModel[] = [];
  const visit = (node: ChartOfAccountsNodeViewModel): void => {
    if (predicate(node)) out.push(node);
    node.Children?.forEach(visit);
  };
  roots.forEach(visit);
  return out;
};

export const ChartOfAccountsStore = signalStore(
  { providedIn: 'root' },
  withState<ChartOfAccountsStoreState>(initialState),
  withReqState(...ALL_OPS),
  withComputed((store) => ({
    roots: computed(() => store.tree()?.Roots ?? []),
    flatLeaves: computed(() =>
      collectNodes(store.tree()?.Roots ?? [], (n) => n.IsLeaf && n.IsActive),
    ),
  })),
  withMethods((store, service = inject(ChartOfAccountsService)) => {
    const loadTree = rxMethod<void>(pipe(
      tap(() => patchState(store, setPropLoading('loadTree'))),
      switchMap(() => service.getTree().pipe(
        tapResponse({
          next: (tree) => {
            patchState(store, { tree }, setPropLoaded('loadTree'));
          },
          error: (error: unknown) => {
            patchState(
              store,
              setPropError('loadTree', error),
            );
          },
        }),
      )),
    ));

    const addLeafNode = rxMethod<AddLeafNodeRequest>(pipe(
      tap(() => patchState(
        store,
        { lastCreatedNodeId: null },
        setPropLoading('addLeafNode'),
      )),
      switchMap((request) => service.addLeafNode(request).pipe(
        tapResponse({
          next: (createdNode) => {
            const currentTree = store.tree();
            const nextTree = currentTree
              ? appendChildInTree(currentTree, request.ParentNodeId, createdNode)
              : currentTree;
            patchState(
              store,
              {
                tree: nextTree,
                lastCreatedNodeId: createdNode?.NodeId ?? null,
              },
              setPropLoaded('addLeafNode'),
            );
          },
          error: (error: unknown) => {
            patchState(
              store,
              setPropError('addLeafNode', error),
            );
          },
        }),
      )),
    ));

    const activateNode = rxMethod<string>(pipe(
      tap(() => patchState(
        store,
        { lastToggledNodeId: null },
        setPropLoading('activateNode'),
      )),
      switchMap((nodeId) => service.activate(nodeId).pipe(
        tapResponse({
          next: () => {
            const currentTree = store.tree();
            const nextTree = currentTree
              ? updateNodeInTree(currentTree, nodeId, (node) => ({ ...node, IsActive: true }))
              : currentTree;
            patchState(
              store,
              { tree: nextTree, lastToggledNodeId: nodeId },
              setPropLoaded('activateNode'),
            );
          },
          error: (error: unknown) => {
            patchState(
              store,
              setPropError('activateNode', error),
            );
          },
        }),
      )),
    ));

    const deactivateNode = rxMethod<string>(pipe(
      tap(() => patchState(
        store,
        { lastToggledNodeId: null },
        setPropLoading('deactivateNode'),
      )),
      switchMap((nodeId) => service.deactivate(nodeId).pipe(
        tapResponse({
          next: () => {
            const currentTree = store.tree();
            const nextTree = currentTree
              ? updateNodeInTree(currentTree, nodeId, (node) => ({ ...node, IsActive: false }))
              : currentTree;
            patchState(
              store,
              { tree: nextTree, lastToggledNodeId: nodeId },
              setPropLoaded('deactivateNode'),
            );
          },
          error: (error: unknown) => {
            patchState(
              store,
              setPropError('deactivateNode', error),
            );
          },
        }),
      )),
    ));

    const getLeavesForDirection = (
      direction: ChartOfAccountsDirection,
    ): ChartOfAccountsNodeViewModel[] => {
      const compatible = direction === 'In' ? IN_NATURES : OUT_NATURES;
      return store.flatLeaves().filter((leaf) => compatible.includes(leaf.Nature));
    };

    const clearMutationState = (): void => {
      patchState(
        store,
        { lastCreatedNodeId: null, lastToggledNodeId: null },
        setPropInit(...MUTATION_OPS),
      );
    };

    const reset = (): void => {
      patchState(store, initialState, setPropInit(...ALL_OPS));
    };

    return {
      loadTree,
      addLeafNode,
      activateNode,
      deactivateNode,
      getLeavesForDirection,
      clearMutationState,
      reset,
    };
  }),
);
